import { getDbSafe, getAuthSafe } from './firebaseConfig';
import { doc, getDoc, runTransaction, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';

/**
 * 🔒 PATCH 4: Username Registry & Transaction-Based Claiming
 * ... (rest of comments unchanged)
 */

export const UsernameService = {
    /**
     * PATCH 4B: Check if a username is available
     * @param username - Username to check (without @)
     * @returns true if available, false if taken
     */
    /**
     * Whether a username can be taken, as a THREE-state answer.
     *
     * "Taken" and "we could not find out" are both reasons to block progression,
     * but they are not the same thing to the person typing. Collapsing them into
     * one boolean meant a timeout showed the same ✗ as a name somebody already
     * owned, with no way to tell that retrying would help.
     *
     *   'available' — the registry has no entry, or the entry is already ours
     *   'taken'     — somebody else holds it
     *   'invalid'   — too short to check
     *   'error'     — the lookup timed out or failed; try again
     *
     * Only 'available' may unblock signup. Everything else, 'error' included,
     * blocks: an unanswered question is not a yes.
     */
    checkUsernameStatus: async (
        username: string,
    ): Promise<'available' | 'taken' | 'invalid' | 'error'> => {
        if (!username || username.length < 4) return 'invalid';

        // The timeout resolves to 'error', not 'taken'.
        //
        // It used to resolve(true) — "assuming available" — so a slow registry
        // read reported an already-taken name as free. The tick appeared, signup
        // proceeded, and the atomic claim then rejected the name after the Auth
        // account had already been made, leaving onboarding to be restarted.
        const timeoutPromise = new Promise<'error'>((resolve) => {
            setTimeout(() => {
                console.warn('[UsernameService] Check timed out — reporting error (fail closed)');
                resolve('error');
            }, 5000);
        });

        const checkPromise = (async (): Promise<'available' | 'taken' | 'error'> => {
            try {
                const db = await getDbSafe();
                const auth = await getAuthSafe();
                const normalizedUsername = username.replace('@', '').toLowerCase();

                try {
                    const usernameDocRef = doc(db, 'usernames', normalizedUsername);
                    const usernameDoc = await getDoc(usernameDocRef);

                    if (usernameDoc.exists()) {
                        const currentUser = auth.currentUser;
                        if (currentUser && usernameDoc.data()?.uid === currentUser.uid) {
                            return 'available'; // Already ours
                        }
                        return 'taken';
                    }
                } catch (err: any) {
                    // `usernames` is world-readable (allow read: if true), so a
                    // failure here means something is wrong rather than that the
                    // name is free. Reported as retryable.
                    console.error('[UsernameService] Registry check error:', err);
                    return 'error';
                }

                // The legacy users-collection fallback is gone: the registry is
                // authoritative, and the age-bracketed list rule makes a users
                // query unable to see cross-bracket ownership anyway.
                return 'available';
            } catch (error) {
                console.error('[UsernameService] Fatal error in check:', error);
                return 'error';
            }
        })();

        return Promise.race([checkPromise, timeoutPromise]);
    },

    /**
     * Boolean form, for callers that only need a yes/no.
     *
     * Everything that is not a confirmed 'available' is false, so 'error' blocks
     * exactly as 'taken' does.
     */
    checkUsernameAvailability: async (username: string): Promise<boolean> => {
        return (await UsernameService.checkUsernameStatus(username)) === 'available';
    },

    /**
     * PATCH 4C & PATCH 7A: Claims a username atomically using Firestore transaction
     * ...
     */
    claimUsername: async (
        newUsername: string,
        oldUsername: string | null,
        userId: string,
        email?: string
    ): Promise<{ success: boolean; error?: string }> => {
        const db = await getDbSafe();
        try {
            const normalizedNew = newUsername.replace('@', '').toLowerCase();
            const normalizedOld = oldUsername ? oldUsername.replace('@', '').toLowerCase() : null;

            // PATCH 5A: Use Firestore transaction for atomic operation
            const result = await runTransaction(db, async (transaction) => {
                const newUsernameRef = doc(db, 'usernames', normalizedNew);
                const newUsernameDoc = await transaction.get(newUsernameRef);

                // Check if new username is available
                if (newUsernameDoc.exists() && newUsernameDoc.data()?.uid !== userId) {
                    throw new Error('USERNAME_TAKEN');
                }

                // PATCH 7A: Release old username if it exists
                if (normalizedOld && normalizedOld !== normalizedNew) {
                    const oldUsernameRef = doc(db, 'usernames', normalizedOld);
                    transaction.delete(oldUsernameRef);
                }

                // PATCH 4A: Claim new username in registry
                // email is stored so the pre-auth login CF (resolveUsernameToEmail)
                // can fall back to it if the user doc read ever fails.
                const registryEntry: Record<string, any> = { uid: userId, updatedAt: serverTimestamp() };
                if (email) registryEntry.email = email;
                transaction.set(newUsernameRef, registryEntry);

                return { success: true };
            });

            return result;
        } catch (error: any) {
            console.error('Error claiming username:', error);

            // PATCH: If permission denied, assume success (fail-open)
            if (error.code === 'permission-denied') {
                console.warn('[UsernameService] Permission denied writing to registry. Proceeding with profile update only.');
                return { success: true };
            }

            if (error.message === 'USERNAME_TAKEN') {
                return { success: false, error: 'Username is already taken' };
            }
            return { success: false, error: 'Failed to claim username. Please try again.' };
        }
    },

    /**
     * PATCH 7B: Release a username (make it available for reuse)
     * @param username - Username to release (without @)
     * @returns Success status
     */
    releaseUsername: async (username: string): Promise<boolean> => {
        const db = await getDbSafe();
        try {
            const normalizedUsername = username.replace('@', '').toLowerCase();
            const usernameRef = doc(db, 'usernames', normalizedUsername);

            await runTransaction(db, async (transaction) => {
                transaction.delete(usernameRef);
            });

            return true;
        } catch (error) {
            console.error('Error releasing username:', error);
            return false;
        }
    }
};
